import { useEffect, useState } from 'react';
import { Wand2, X } from 'lucide-react';
import { toast } from 'sonner';
import { contactsAPI } from '../../lib/api';
import { shouldShowNameCleanupPrompt, suspiciousNameContacts } from '../../lib/memoryJogNames';
import { Button } from '../ui/button';
import { NameHygieneReviewModal } from '../add-contacts/manage-dialogs';

const DISMISS_KEY = 'pik_name_cleanup_prompt_dismissed';

function dismissed() {
  try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
}

function rememberDismiss() {
  try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode */ }
}

export default function NameCleanupPrompt({ contacts, selfId }) {
  const [hidden, setHidden] = useState(dismissed);
  const [scanning, setScanning] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [results, setResults] = useState(null);
  const matches = suspiciousNameContacts(contacts, selfId);

  useEffect(() => {
    if (!scanning || !jobId) return undefined;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await contactsAPI.getEnrichJob(jobId);
        if (stop || !data) return;
        if (data.status === 'completed') {
          setScanning(false);
          const names = data.results?.per_stage?.names;
          if (!names || names.error) {
            toast.error('Name clean-up did not finish. You can try again.');
          } else if ((names.auto_applied_count || 0) === 0 && (names.needs_review_count || 0) === 0) {
            toast.success('Those names look clean.');
            setHidden(true);
          } else {
            setResults(names);
          }
        } else if (data.status === 'failed') {
          setScanning(false);
          toast.error('Name clean-up did not finish. You can try again.');
        }
      } catch { /* keep polling */ }
    };
    const timer = setInterval(tick, 2000);
    tick();
    return () => { stop = true; clearInterval(timer); };
  }, [scanning, jobId]);

  if (!shouldShowNameCleanupPrompt(contacts, selfId, hidden)) return null;

  const start = async () => {
    setScanning(true);
    try {
      const { data } = await contactsAPI.nameHygieneScan(matches.map((contact) => contact.id));
      setJobId(data?.job_id || null);
      if (!data?.job_id) {
        setScanning(false);
        toast.error('Could not start name clean-up.');
      }
    } catch (err) {
      setScanning(false);
      toast.error(err?.response?.data?.detail || 'Could not start name clean-up.');
    }
  };

  return (
    <>
      <div
        className="w-full flex items-start gap-2.5 px-3 py-2 rounded-xl bg-violet-50 border border-violet-200"
        data-testid="name-cleanup-prompt"
      >
        <span className="w-6 h-6 rounded-full bg-card flex items-center justify-center shrink-0">
          <Wand2 size={14} className="text-violet-700" />
        </span>
        <button type="button" className="flex-1 text-left" onClick={start} disabled={scanning} data-testid="name-cleanup-prompt-open">
          <span className="block text-sm font-semibold text-foreground">Clean up names</span>
          <span className="block text-xs text-muted-foreground mt-0.5">
            Some names have notes mixed in. Review suggestions before PeopleiKnow saves them.
          </span>
        </button>
        <button
          type="button"
          aria-label="Dismiss"
          data-testid="name-cleanup-prompt-dismiss"
          className="text-muted-foreground shrink-0"
          onClick={() => { rememberDismiss(); setHidden(true); }}
        >
          <X size={15} />
        </button>
      </div>

      <NameHygieneReviewModal
        open={!!results}
        results={results}
        onClose={() => setResults(null)}
        onApply={async (item) => {
          await contactsAPI.nameHygieneApply({
            contact_id: item.contact_id,
            first_name: item.first_name,
            middle_name: item.middle_name,
            last_name: item.last_name,
            descriptor: item.descriptor,
            descriptor_type: item.descriptor_type,
          });
          setResults((prev) => prev && ({
            ...prev,
            needs_review: prev.needs_review.filter((row) => row.contact_id !== item.contact_id),
            needs_review_count: Math.max(0, (prev.needs_review_count || 0) - 1),
          }));
        }}
        onSkip={(item) => {
          contactsAPI.nameHygieneSkip({
            descriptor: item.descriptor, descriptor_type: item.descriptor_type,
          }).catch(() => {});
          setResults((prev) => prev && ({
            ...prev,
            needs_review: prev.needs_review.filter((row) => row.contact_id !== item.contact_id),
            needs_review_count: Math.max(0, (prev.needs_review_count || 0) - 1),
          }));
        }}
        onRevert={async (contactId) => {
          await contactsAPI.nameHygieneRevert(contactId);
        }}
      />
    </>
  );
}
